import _ from 'lodash'
import { createSelector } from 'reselect'

import { FORMATIVE_REPORT_TRACKING_MODE } from 'services/legacy/formativeReports/constants'
import { Membership } from 'services/legacy/memberships/models'
import { NurserySettingsLikesAndComments } from 'services/nurseries/models'

import { RootState } from 'core/reducers'

export const getNurseryContext = (state: RootState) => state.nurseryContext.common.data

const getNurserySettings = createSelector(
  [getNurseryContext],
  (nursery) => nursery?.nurserySettings,
)

export const getNurseryLearningSettings = createSelector(
  [getNurserySettings],
  (nurserySettings) => {
    if (!nurserySettings || !nurserySettings.learning) {
      return null
    }

    const copyNurserySettings = { ...nurserySettings }

    if (copyNurserySettings.learning && !copyNurserySettings.learning.formativeReportsTrackingMode) {
      copyNurserySettings.learning.formativeReportsTrackingMode = FORMATIVE_REPORT_TRACKING_MODE.MANUAL
    }

    return copyNurserySettings.learning
  },
)

export const getNurseryOpeningDays = createSelector(
  [getNurserySettings],
  (nursery) => nursery?.openingDays,
)

export const getNurseryAiSettings = createSelector(
  [getNurserySettings],
  (nursery) => nursery?.ai,
)

export const getSessionExpireTimeEnabled = createSelector([getNurserySettings], (state) => (
  state?.security.sessionExpireTimeEnabled
))

export const getSessionExpireTime = createSelector([getNurserySettings], (state) => (
  state?.security.sessionExpireTime
))

export const getNurseryStripeIntegration = createSelector(
  [getNurseryContext],
  (state) => state?.stripeNurseryIntegration,
)

export const transformObject = (data) => _.transform(data, (acc, value, key) => {
  if (_.isArray(value)) {
    // eslint-disable-next-line no-param-reassign
    value = _.pickBy(_.fromPairs(
      value.map((v, i) => [i, v]),
    ), (v) => null !== v)
  }

  if (_.isPlainObject(value)) {
    // eslint-disable-next-line no-param-reassign
    value = transformObject(value)
  }

  if (Number.isInteger(Number(key))) {
    acc[value] = true
  } else {
    acc[key] = value
  }
}, {})

export const getNurseryFormBuilderSettings = createSelector(
  [getNurserySettings],
  (state) => {
    if (!state?.formBuilder) {
      return null
    }

    return transformObject(state.formBuilder)
  },
)

export const getMembersWithAccessToLikesAndComments = createSelector(
  [getNurseryContext],
  (state): Membership[] => (
    state?.likeAndCommentMembers || []
  ),
)

export const getNurseryLikeAndCommentSettings = createSelector(
  [getNurserySettings],
  (nurserySettings): NurserySettingsLikesAndComments | null => {
    if (!nurserySettings?.likeAndComment) {
      return null
    }

    return nurserySettings.likeAndComment
  },
)

export const getAnnualizedInvoiceSettings = createSelector([getNurserySettings], (state) => (
  state?.invoice?.annualisedInvoiceSettings
))
